import React, { useEffect, useState } from "react";
import {
    getStates,
    fetchDistrictsByState,
    getCollegesData,
    getDepartment,
    getCollegeByAisheCode,
    createAisheEntry
} from "../api/fetch-masters";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import EmailOTP from "../modal/EmailOTP";

const getInitialEmailVerification = () => ({
    show: false,
    verified: false,
    email: "",
    verifiedEmail: "",
    displayEmail: "",
});

const getInitialEmailMatchPrompt = () => ({
    show: false,
    enteredEmail: "",
    actualEmail: "",
    displayEmail: "",
    error: "",
});

const sanitizeSearchText = (value) =>
    String(value || "").replace(/[^A-Za-z0-9\s]/g, "");

const sanitizeAisheText = (value) =>
    String(value || "").replace(/[^A-Za-z0-9-]/g, "").slice(0, 7).toUpperCase();

export default function InstituteDashboard() {
    const navigate = useNavigate();

    const [colleges, setColleges] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [districtsList, setDistrictsList] = useState([]);
    const [loading, setLoading] = useState(false);

    const [collegeExists, setCollegeExists] = useState("");
    const [departmentsList, setDepartmentsList] = useState({});
    const [aisheCode, setAisheCode] = useState("");
    const [aisheLoading, setAisheLoading] = useState(false);
    const [aisheError, setAisheError] = useState("");
    const [aisheCollege, setAisheCollege] = useState(null);
    const [aisheSubmitting, setAisheSubmitting] = useState(false);
    const [showAisheSuccess, setShowAisheSuccess] = useState(false);
    const [emailVerification, setEmailVerification] = useState(getInitialEmailVerification);
    const [emailMatchPrompt, setEmailMatchPrompt] = useState(getInitialEmailMatchPrompt);

    const [filters, setFilters] = useState({
        stateId: "",
        districtId: "",
        name: "",
    });

    const [tableFilters, setTableFilters] = useState({
        department: "",
        collegeName: "",
        aisheCodeFilter: "",
    });

    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasSearched, setHasSearched] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 20,
        lastPage: 1,
        totalCount: 0,
    });

    const sortByName = (data = []) =>
        [...data].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        );

    /* ===============================
       Fetch States
    =============================== */
    useEffect(() => {
        const loadStates = async () => {
            const states = await getStates();
            setStatesList(sortByName(states || []));
        };
        loadStates();
    }, []);

    /* ===============================
       Fetch Districts on State Change
    =============================== */
    useEffect(() => {
        if (!filters.stateId) {
            setDistrictsList([]);
            setFilters((prev) => ({ ...prev, districtId: "" }));
            return;
        }
        const loadDistricts = async () => {
            const districts = await fetchDistrictsByState(filters.stateId);
            setDistrictsList(sortByName(districts || []));
        };
        loadDistricts();
    }, [filters.stateId]);

    /* ===============================
       Handle Search
    =============================== */
    const handleSearch = async () => {
        if (!filters.stateId || !filters.districtId) {
            alert("Please select State and District");
            return;
        }
        setLoading(true);
        setHasSearched(true);
        setCollegeExists("");
        setAisheCode("");
        setAisheError("");
        setAisheCollege(null);
        setEmailMatchPrompt(getInitialEmailMatchPrompt());
        setEmailVerification(getInitialEmailVerification());
        setTableFilters({ department: "", collegeName: "", aisheCodeFilter: "" });

        try {
            const data = await getCollegesData(filters.stateId, filters.districtId, filters.name);
            const collegeItems = data?.items || [];
    const department = await getDepartment();
        const departmentMap = {};
        department.forEach((dept) => {
            departmentMap[dept.id] = dept.name;
            departmentMap[parseInt(dept.departmentId)] = dept.name;
 
        });
        setDepartmentsList(departmentMap);
            setColleges(collegeItems);
            setCurrentPage(1);
            setPagination({
                page: 1,
                pageSize: rowsPerPage,
                lastPage: 1,
                totalCount: collegeItems.length,
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        const totalPages = Math.max(1, Math.ceil(filteredColleges.length / rowsPerPage));
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (event) => {
        const nextPageSize = Number(event.target.value) || 20;
        setRowsPerPage(nextPageSize);
        setCurrentPage(1);
        setPagination((prev) => ({
            ...prev,
            page: 1,
            pageSize: nextPageSize,
        }));
    };

    /* ===============================
       Reset
    =============================== */
    const handleReset = () => {
        setFilters({ stateId: "", districtId: "", name: "" });
        setDistrictsList([]);
        setColleges([]);
        setDepartmentsList({});
        setCollegeExists("");
        setAisheCode("");
        setAisheError("");
        setAisheCollege(null);
        setTableFilters({ department: "", collegeName: "", aisheCodeFilter: "" });
        setCurrentPage(1);
        setHasSearched(false);
        setPagination({
            page: 1,
            pageSize: rowsPerPage,
            lastPage: 1,
            totalCount: 0,
        });
        setEmailMatchPrompt(getInitialEmailMatchPrompt());
        setEmailVerification(getInitialEmailVerification());
    };

    /* ===============================
        Filtered colleges (client-side)
    =============================== */
    const filteredColleges = colleges.filter((college) => {
        
        const deptName = departmentsList[college.departmentId]?.toLowerCase() || "";

        const colName = college.name?.toLowerCase() || "";

        const combinedValues = [
            deptName,
            college.name,
            college.website,
            college.aisheCode,
            college.address,
            college.collegeContactNumber,
        ].filter(Boolean).join(" ").toLowerCase();

        const matchDept = tableFilters.department
            ? deptName === tableFilters.department.toLowerCase()
            : true;

        const matchName = tableFilters.collegeName
            ? colName.includes(tableFilters.collegeName.toLowerCase())
            : true;

        let matchAishe = true;

        if (tableFilters.aisheCodeFilter) {
            const searchTerms = tableFilters.aisheCodeFilter
            .toLowerCase().split("|").map((t) => t.trim()).filter(Boolean);

            matchAishe = searchTerms.some((term) =>
            combinedValues.includes(term)
            );
        }

        return matchDept && matchName && matchAishe;
    });

    // Local pagination on fully fetched dataset
    const totalFilteredPages = Math.max(1, Math.ceil(filteredColleges.length / rowsPerPage));
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedData = filteredColleges.slice(startIndex, startIndex + rowsPerPage);

    //  Unique department options for Select dropdown
    const departmentOptions = [
    ...new Map(
        colleges
        .map((c) => String(departmentsList[c.departmentId] || "").trim())
        .filter(Boolean)
        .map((departmentName) => [
            departmentName.toLowerCase(), { value: departmentName, label: departmentName },
        ])
    ).values(),
    ].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );

    const maskEmail = (value) => {
        const email = String(value || "").trim();
        if (!email || !email.includes("@")) return "";
        const [local, domain] = email.split("@");
        const maskedLocal =
            local.length <= 2
                ? `${local[0] || ""}*`
                : `${local[0]}${"*".repeat(Math.max(1, local.length - 2))}${local[local.length - 1]}`;
        const domainParts = domain.split(".");
        const domainName = domainParts[0] || "";
        const maskedDomain =
            domainName.length <= 2
                ? `${domainName[0] || ""}*`
                : `${domainName[0]}${"*".repeat(Math.max(1, domainName.length - 2))}${domainName[domainName.length - 1]}`;
        const domainSuffix = domainParts.slice(1).join(".");
        return `${maskedLocal}@${maskedDomain}${domainSuffix ? `.${domainSuffix}` : ""}`;
    };

    const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

    const handleAisheSearch = async () => {
        const code = String(aisheCode || "").trim();
        if (!code) { alert("Please enter AISHE Code"); return; }

        setAisheLoading(true);
        setAisheError("");
        setEmailMatchPrompt(getInitialEmailMatchPrompt());
        setEmailVerification(getInitialEmailVerification());
        try {
            const college = await getCollegeByAisheCode(code);
            if (!college) {
                setAisheError("No institute found for the given AISHE Code.");
                setAisheLoading(false);
                return;
            }
            const screenName = String(college.screenName || "").trim();
            const email = college.collegeEmailAddress || college.email || college.instituteEmail || college.emailId || "";
            if (!email) {
                setAisheError("Email not found for this institute.");
                setAisheLoading(false);
                return;
            }
            setAisheCollege({ ...college, screenName });
            setEmailMatchPrompt({
                show: true,
                enteredEmail: "",
                actualEmail: String(email).trim(),
                displayEmail: maskEmail(email),
                error: "",
            });
        } catch (error) {
            console.error("AISHE search flow failed:", error);
            setAisheError("Failed to process AISHE Code search.");
        } finally {
            setAisheLoading(false);
        }
    };

    const handleEmailMatchClose = () => {
        setEmailMatchPrompt(getInitialEmailMatchPrompt());
    };

    const handleEmailMatchConfirm = (event) => {
        event.preventDefault();

        const enteredEmail = String(emailMatchPrompt.enteredEmail || "").trim();
        const actualEmail = String(emailMatchPrompt.actualEmail || "").trim();

        if (!enteredEmail) {
            setEmailMatchPrompt((prev) => ({
                ...prev,
                error: "Please enter email address.",
            }));
            return;
        }

        if (!actualEmail) {
            setEmailMatchPrompt((prev) => ({
                ...prev,
                error: "Registered institute email not found.",
            }));
            return;
        }

        if (normalizeEmail(enteredEmail) !== normalizeEmail(actualEmail)) {
            setEmailMatchPrompt((prev) => ({
                ...prev,
                error: "Entered email does not match the registered institute email.",
            }));
            return;
        }

        setEmailMatchPrompt(getInitialEmailMatchPrompt());
        setEmailVerification({
            show: true,
            verified: false,
            email: actualEmail,
            verifiedEmail: "",
            displayEmail: maskEmail(actualEmail),
        });
    };

    useEffect(() => {
        const handleAisheVerification = async () => {
            if (!emailVerification.verified || !aisheCollege) return;
            const screenName = String(aisheCollege.screenName || "").trim();
            if (!screenName) {
                setAisheError("Institute not found for this AISHE Code.");
                setEmailVerification((prev) => ({ ...prev, verified: false }));
                return;
            }
            const payload = {
                aisheCode: String(aisheCollege.aisheCode || aisheCode || "").trim(),
                email: String(emailVerification.email || "").trim(),
                screenName,
            };
            setAisheSubmitting(true);
            try {
                await createAisheEntry(payload);
                setCollegeExists("");
                setAisheCode("");
                setAisheError("");
                setAisheCollege(null);
                setEmailVerification({ show: false, verified: false, email: "", verifiedEmail: "", displayEmail: "" });
                setShowAisheSuccess(true);
            } catch (error) {
                console.error("Failed to store AISHE details:", error);
                setAisheError("Failed to store AISHE details.");
                setEmailVerification((prev) => ({ ...prev, verified: false }));
            } finally {
                setAisheSubmitting(false);
            }
        };
        handleAisheVerification();
    }, [emailVerification.verified, emailVerification.email, aisheCollege, aisheCode]);

    const getVisiblePages = () => {
        const totalPages = totalFilteredPages;
        const maxVisible = 7;
        if (totalPages <= maxVisible) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const pages = [1];
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        if (start > 2) pages.push("left-ellipsis");
        for (let page = start; page <= end; page++) {
            pages.push(page);
        }
        if (end < totalPages - 1) pages.push("right-ellipsis");

        pages.push(totalPages);
        return pages;
    };

    const pageButtons = getVisiblePages();

    useEffect(() => {
        setCurrentPage(1);
    }, [tableFilters.department, tableFilters.collegeName, tableFilters.aisheCodeFilter, rowsPerPage]);

    return (
        <div className="container mt-4">
          <div class="terms-card">
            <h4 className="terms-card-head">Search Institute</h4>
            <div class="terms-card-body">
            <div className="alert alert-info py-2">
                Note: Before New Institute Registration, Kindly check if institute is already registered or not?
            </div>

            {/* ================= SEARCH CARD ================= */}
            <div className="card shadow-sm p-4 mb-4">
                <div className="row">
                    <div className="col-md-4 mb-3">
                        <label className="form-label">State<span className="text-danger">*</span></label>
                        <Select
                            options={statesList.map((s) => ({ value: s.id, label: s.name }))}
                            value={statesList.map((s) => ({ value: s.id, label: s.name })).find((opt) => opt.value === filters.stateId) || null}
                            onChange={(selected) => setFilters({ ...filters, stateId: selected ? selected.value : "", districtId: "" })}
                            placeholder="Select State"
                            isClearable
                        />
                    </div>
                    <div className="col-md-4 mb-3">
                        <label className="form-label">District<span className="text-danger">*</span></label>
                        <Select
                            options={districtsList.map((d) => ({ value: d.id, label: d.name }))}
                            value={districtsList.map((d) => ({ value: d.id, label: d.name })).find((opt) => opt.value === filters.districtId) || null}
                            onChange={(selected) => setFilters({ ...filters, districtId: selected ? selected.value : "" })}
                            placeholder="Select District"
                            isClearable
                            isDisabled={!filters.stateId}
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Institute</label>
                        <input
                            type="text"
                            className="form-control"
                            value={filters.name}
                            onChange={(e) => setFilters({ ...filters, name: sanitizeSearchText(e.target.value) })}
                            placeholder="Enter Institute Name/AISHE Code"
                        />
                    </div>
                </div>
                <div className="mt-3 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleSearch}>
                        Search College Data
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleReset}>
                        Reset
                    </button>
                </div>
            </div>
            </div>
            </div>

            {/* ================= NEW REGISTRATION BUTTON ================= */}
            <div className="d-flex justify-content-end mb-3 mt-3">
                <button className="btn btn-primary" onClick={() => navigate("/register")}>
                    New Institute Registration
                </button>
            </div>

            {/* ================= TABLE CARD ================= */}
            <div className="terms-card">
                <div className="terms-card-head">
                    <h6 className="mb-0 fw-bold">Institute List</h6>
                    {/*  Total count badge */}
                    {hasSearched && (
                        <span className="badge bg-secondary">
                            {filteredColleges.length} of {pagination.totalCount} records
                        </span>
                    )}
                </div>

                {/*  TABLE FILTER ROW — shown only after search */}
                <div className="p-3 border-bottom bg-light">
                    <div className="row g-2 align-items-end">
                        {/* Department Filter */}
                        <div className="col-md-4">
                            <Select
                                options={departmentOptions}
                                value={departmentOptions.find((o) => o.value === tableFilters.department) || null}
                                onChange={(selected) => {
                                    setTableFilters((prev) => ({ ...prev, department: selected ? selected.value : "" }));
                                }}
                                placeholder="All Departments"
                                isClearable
                            />
                        </div>

                        {/* College Name Filter */}
                        <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search college name..."
                                value={tableFilters.collegeName}
                                onChange={(e) => {
                                    setTableFilters((prev) => ({ ...prev, collegeName: sanitizeSearchText(e.target.value) }));
                                }}
                            />
                            
                        </div>

                        {/* AISHE Code Filter */}
                        <div className="col-md-3">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search ..."
                                value={tableFilters.aisheCodeFilter}
                                onChange={(e) => {
                                    setTableFilters((prev) => ({ ...prev, aisheCodeFilter: sanitizeSearchText(e.target.value) }));
                                }}
                            />
                        </div>

                        {/* Clear Filters */}
                        <div className="col-md-1">
                            <button
                                className="btn btn-outline-secondary btn-sm w-100  text-white"
                                onClick={() => {
                                    setTableFilters({ department: "", collegeName: "", aisheCodeFilter: "" });
                                }}
                                title="Clear table filters"
                            >  Reset </button>
                        </div>
                    </div>
                </div>

                {/* ================= TABLE ================= */}
                <div class="terms-card-body">
                <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                        <thead className="table-primary text-center">
                            <tr>
                                <th>#</th>
                                <th>Department</th>
                                <th>College Name</th>
                                <th>Website</th>
                                <th>AISHE Code</th>
                                <th>Address</th>
                                <th>Contact No</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center">Loading...</td></tr>
                            ) : paginatedData.length > 0 ? (
                                paginatedData.map((college, index) => (
                                    <tr key={college.id}>
                                        <td>{startIndex + index + 1}</td>
                                        <td>{departmentsList[college.departmentId] || "-"}</td>
                                        <td>{college.name}</td>
                                        <td>{college.website || "-"}</td>
                                        <td>{college.aisheCode || "-"}</td>
                                        <td>{college.address || "-"}</td>
                                        <td>{college.collegeContactNumber || "-"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="7" className="text-center">No data found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                </div>

                {/* ================= RADIO + BUTTON ================= */}
                {hasSearched && !loading && (
                    <div className="p-3 border-top d-flex justify-content-between align-items-center">
                        <div>
                            <label className="fw-semibold">
                                Does your college/school exist in above list?<span className="text-danger">*</span>
                            </label>
                            <div className="mt-2">
                                <input
                                    type="radio" value="yes"
                                    checked={collegeExists === "yes"}
                                    onChange={(e) => {
                                        setCollegeExists(e.target.value);
                                        setAisheCode(""); setAisheError(""); setAisheCollege(null);
                                        setEmailMatchPrompt(getInitialEmailMatchPrompt());
                                        setEmailVerification(getInitialEmailVerification());
                                    }}
                                />{" "}Yes
                                <input
                                    type="radio" value="no" className="ms-3"
                                    checked={collegeExists === "no"}
                                    onChange={(e) => {
                                        setCollegeExists(e.target.value);
                                        setAisheCode(""); setAisheError(""); setAisheCollege(null);
                                        setEmailMatchPrompt(getInitialEmailMatchPrompt());
                                        setEmailVerification(getInitialEmailVerification());
                                    }}
                                />{" "}No
                            </div>
                        </div>
                        {collegeExists === "no" && (
                            <button className="btn btn-primary" onClick={() => navigate("/register")}>
                                New Institute Registration
                            </button>
                        )}
                    </div>
                )}

                {hasSearched && !loading && collegeExists === "yes" && (
                    <div className="p-3 border-top">
                        <div className="row align-items-end">
                            <div className="col-md-6 mb-2">
                                <label className="form-label fw-semibold">AISHE Code</label>
                                <input
                                    type="text" className="form-control"
                                    value={aisheCode}
                                    onChange={(e) => setAisheCode(sanitizeAisheText(e.target.value))}
                                    placeholder="Enter AISHE Code"
                                />
                            </div>
                            <div className="col-md-3 mb-2">
                                <button
                                    type="button" className="btn btn-primary"
                                    onClick={handleAisheSearch}
                                    disabled={aisheLoading || aisheSubmitting}
                                >
                                    {aisheLoading || aisheSubmitting ? "Please wait..." : "Search AISHE"}
                                </button>
                            </div>
                            {aisheError && <div className="text-danger small mt-1">{aisheError}</div>}
                        </div>
                    </div>
                )}

                {/* ================= PAGINATION ================= */}
                {totalFilteredPages > 1 && (
                    <div className="p-3 border-top d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <label className="text-muted mb-0">Rows per page:</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: "90px" }}
                                value={rowsPerPage}
                                onChange={handleRowsPerPageChange}
                                disabled={loading}
                            >
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={70}>70</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="text-muted">
                            Showing {filteredColleges.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + paginatedData.length, filteredColleges.length)} of {filteredColleges.length} records
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                disabled={currentPage === 1 || loading}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                Previous
                            </button>
                            <div className="d-flex gap-2">
                                {pageButtons.map((pageNumber, index) =>
                                    typeof pageNumber === "number" ? (
                                        <button
                                            key={pageNumber}
                                            className={`btn btn-sm ${currentPage === pageNumber ? "btn-primary" : "btn-outline-secondary"}`}
                                            disabled={loading}
                                            onClick={() => handlePageChange(pageNumber)}
                                        >
                                            {pageNumber}
                                        </button>
                                    ) : (
                                        <span key={`${pageNumber}-${index}`} className="align-self-center px-1 text-muted">
                                            ...
                                        </span>
                                    )
                                )}
                            </div>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                disabled={currentPage === totalFilteredPages || loading}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

                {totalFilteredPages <= 1 && hasSearched && !loading && (
                    <div className="p-3 border-top text-muted">
                        Showing {filteredColleges.length === 0 ? 0 : 1}-
                        {filteredColleges.length} of {filteredColleges.length} records
                    </div>
                )}
            </div>

            {emailMatchPrompt.show && (
                <>
                    <div className="modal-backdrop fade show" style={{ position: "fixed", inset: 0, zIndex: 1050 }} />
                    <div className="modal fade show d-block" tabIndex="-1" aria-modal="true" role="dialog" style={{ position: "fixed", inset: 0, zIndex: 1055 }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "420px", width: "100%" }}>
                            <div className="modal-content shadow-lg border-0 rounded-2 overflow-hidden">
                                <div className="modal-header bg-primary text-white py-2 px-3">
                                    <h5 className="modal-title fw-bold mb-0 fs-6">Verify Institute Email</h5>
                                    <button className="btn-close btn-close-white" onClick={handleEmailMatchClose} />
                                </div>
                                <form onSubmit={handleEmailMatchConfirm}>
                                    <div className="modal-body p-3" style={{ background: "#f8f9fc" }}>
                                        <p className="text-muted small mb-3">
                                            Enter your institute email ID. OTP will be sent only if it matches the registered email for this AISHE code.
                                        </p>
                                        <div className=" d-inline-block bg-white p-2 rounded-1 shadow-sm border" role="alert">
                                            Registered email: {emailMatchPrompt.displayEmail}
                                        </div>
                                        <label className="form-label fw-semibold mb-1">Email Address</label>
                                        <input
                                            type="email"
                                            className={`form-control ${emailMatchPrompt.error ? "is-invalid" : ""}`}
                                            value={emailMatchPrompt.enteredEmail}
                                            onChange={(event) =>
                                                setEmailMatchPrompt((prev) => ({
                                                    ...prev,
                                                    enteredEmail: event.target.value,
                                                    error: "",
                                                }))
                                            }
                                            placeholder="Enter email address"
                                            autoFocus
                                        />
                                        {emailMatchPrompt.error && (
                                            <div className="invalid-feedback d-block">{emailMatchPrompt.error}</div>
                                        )}
                                    </div>
                                    <div className="modal-footer border-0 p-2 bg-light">
                                        <button type="button" className="btn btn-outline-secondary rounded-1 px-3 py-1" onClick={handleEmailMatchClose}>
                                            Close
                                        </button>
                                        <button type="submit" className="btn btn-primary rounded-1 px-3 py-1">
                                            Continue
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {emailVerification.show && (
                <EmailOTP data={emailVerification} setEmailVerification={setEmailVerification} />
            )}

            {showAisheSuccess && (
                <>
                    <div className="modal-backdrop fade show" style={{ position: "fixed", inset: 0, zIndex: 1050 }} />
                    <div className="modal fade show d-block" tabIndex="-1" aria-modal="true" role="dialog" style={{ position: "fixed", inset: 0, zIndex: 1055 }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "420px", width: "100%" }}>
                            <div className="modal-content shadow-lg border-0 rounded-2 overflow-hidden">
                                <div className="modal-header bg-primary text-white py-2 px-3">
                                    <h5 className="modal-title fw-bold mb-0 fs-6">Success</h5>
                                    <button className="btn-close btn-close-white" onClick={() => setShowAisheSuccess(false)} />
                                </div>
                                <div className="modal-body p-3 text-center" style={{ background: "#f8f9fc" }}>
                                    <p className="mb-0 text-muted">Login information is shared to your registered email id.</p>
                                </div>
                                <div className="modal-footer border-0 p-2 bg-light justify-content-center">
                                    <button className="btn btn-primary rounded-1 px-4 py-1" onClick={() => setShowAisheSuccess(false)} style={{ fontSize: "0.85rem" }}>OK</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
